/* ========================================
   Product Recommendations Tabs
   - Tab switching
   - Slider (snap + arrows + wheel)
   - Recently Viewed (localStorage + Section Rendering API)
   ======================================== */

(function () {
  'use strict';

  const STORAGE_KEY = 'prt_recently_viewed';
  const RECENTLY_SECTION = 'recently-viewed-products';

  class ProductRecommendationsTabs {
    constructor(root) {
      this.root = root;
      this.productId = root.dataset.productId;
      this.sectionId = root.dataset.sectionId;
      this.recentlySectionId = root.dataset.recentlySectionId || RECENTLY_SECTION;
      this.limit = parseInt(root.dataset.productsLimit, 10) || 8;

      this.tabs = root.querySelectorAll('[data-prt-tab]');
      this.panels = root.querySelectorAll('[data-prt-panel]');

      this.init();
    }

    init() {
      this.bindTabs();
      this.initSliders();
      this.maybeLoadRecommendations();
      // 初期表示はRecommend。Recentlyはタブ初回表示時にlazy load
    }

    /* ---------- Tabs ---------- */
    bindTabs() {
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.activateTab(tab));
      });
    }

    activateTab(tab) {
      const target = tab.dataset.prtTab;

      this.tabs.forEach((t) => {
        const active = t === tab;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      this.panels.forEach((panel) => {
        const isTarget = panel.dataset.prtPanel === target;
        panel.classList.toggle('is-active', isTarget);
        if (isTarget) {
          panel.removeAttribute('hidden');
        } else {
          panel.setAttribute('hidden', '');
        }
      });

      if (target === 'recently') {
        this.loadRecentlyViewed();
      }

      // タブ切替後にナビ表示判定を再実行
      const slider = this.root.querySelector(
        `[data-prt-panel="${target}"] [data-prt-slider]`
      );
      if (slider && slider._prtSliderInstance) {
        slider._prtSliderInstance.update();
      }
    }

    /* ---------- Sliders ---------- */
    initSliders() {
      const sliders = this.root.querySelectorAll('[data-prt-slider]');
      sliders.forEach((slider) => {
        const wrap = slider.closest('.prt-slider-wrap');
        const prev = wrap.querySelector('[data-prt-prev]');
        const next = wrap.querySelector('[data-prt-next]');
        slider._prtSliderInstance = new PrtSlider(slider, prev, next);
      });
    }

    /* ---------- Recommendations (fallback fetch) ---------- */
    maybeLoadRecommendations() {
      const fetchUrlEl = this.root.parentElement
        ? this.root.parentElement.querySelector('[data-prt-fetch-url]') ||
          document.querySelector(`[data-prt-fetch-url]`)
        : null;

      // セクションタグ内に script[data-prt-fetch-url] が存在する場合のみフェッチ
      const scripts = document.querySelectorAll('[data-prt-fetch-url]');
      let url = null;
      scripts.forEach((s) => {
        try {
          const parsed = JSON.parse(s.textContent);
          if (parsed && parsed.indexOf('product_id=' + this.productId) !== -1) {
            url = parsed;
          }
        } catch (e) {}
      });

      if (!url) return;

      fetch(url)
        .then((r) => r.text())
        .then((html) => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const newSection = doc.querySelector(`[data-prt-section]`);
          if (!newSection) return;

          const newSlider = newSection.querySelector(
            '[data-prt-panel="recommend"] [data-prt-slider]'
          );
          const currentSlider = this.root.querySelector(
            '[data-prt-panel="recommend"] [data-prt-slider]'
          );
          if (newSlider && currentSlider) {
            currentSlider.innerHTML = newSlider.innerHTML;
            if (currentSlider._prtSliderInstance) {
              currentSlider._prtSliderInstance.update();
            }
          }
        })
        .catch(() => {});
    }

    /* ---------- Recently Viewed ---------- */
    loadRecentlyViewed() {
      const slider = this.root.querySelector(
        '[data-prt-recently-slider]'
      );
      const emptyEl = slider.querySelector('[data-prt-empty]');

      if (slider.dataset.loaded === 'true') return;

      let list = [];
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        list = raw ? JSON.parse(raw) : [];
      } catch (e) {
        list = [];
      }

      // 現在の商品ハンドルを除外
      const currentHandle = window.location.pathname.match(
        /\/products\/([^\/?#]+)/
      );
      const currentHandleStr = currentHandle ? currentHandle[1] : null;

      list = list.filter(
        (item) => item && item.handle && item.handle !== currentHandleStr
      );

      // 表示数で制限
      list = list.slice(0, this.limit);

      if (list.length === 0) {
        if (emptyEl) emptyEl.removeAttribute('hidden');
        slider.dataset.loaded = 'true';
        return;
      }

      // 各商品のカードHTMLを Section Rendering API から取得
      Promise.all(
        list.map((item) =>
          fetch(`/products/${item.handle}?section_id=${this.recentlySectionId}`)
            .then((r) => (r.ok ? r.text() : null))
            .catch(() => null)
        )
      ).then((results) => {
        if (emptyEl) emptyEl.setAttribute('hidden', '');

        const fragment = document.createDocumentFragment();
        results.forEach((html) => {
          if (!html) return;
          const wrapper = document.createElement('div');
          wrapper.innerHTML = html.trim();
          const card = wrapper.querySelector('[data-prt-recently-card]');
          if (card) fragment.appendChild(card);
        });

        // empty メッセージ以外をクリアして追加
        Array.from(slider.children).forEach((child) => {
          if (!child.hasAttribute('data-prt-empty')) {
            slider.removeChild(child);
          }
        });
        slider.appendChild(fragment);
        slider.dataset.loaded = 'true';

        if (slider._prtSliderInstance) {
          slider._prtSliderInstance.update();
        }
      });
    }
  }

  /* ========================================
     Slider Class
     ======================================== */
  class PrtSlider {
    constructor(slider, prevBtn, nextBtn) {
      this.slider = slider;
      this.prevBtn = prevBtn;
      this.nextBtn = nextBtn;
      this.wrap = slider.closest('.prt-slider-wrap');

      this.bindEvents();
      this.update();
    }

    bindEvents() {
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.scroll(-1));
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.scroll(1));
      }

      this.slider.addEventListener('scroll', () => this.updateButtons(), {
        passive: true,
      });

      // マウスホイール横スクロール (PCのみ)
      // - 横方向のホイール(トラックパッド横スワイプ等)はそのままブラウザに任せる
      // - 縦方向のホイールはページの縦スクロールを優先し、スライダーの横送りには使わない
      //   (ページスクロールがブロックされるのを防ぐため)
      this.slider.addEventListener(
        'wheel',
        (e) => {
          if (window.matchMedia('(max-width: 749px)').matches) return;

          // 横方向のホイール入力(トラックパッドの横スワイプ等)があれば、それを横スクロールに使う
          if (Math.abs(e.deltaX) > Math.abs(e.deltaY) && e.deltaX !== 0) {
            // ブラウザのデフォルトの横スクロール動作に任せるので preventDefault しない
            return;
          }
          // 縦方向ホイールの場合は何もしない(=ページが普通に縦スクロールする)
        },
        { passive: true }
      );

      // リサイズ時に再計算
      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => this.update(), 150);
      });
    }

    getStep() {
      const slide = this.slider.querySelector('.prt-slide');
      if (!slide) return this.slider.clientWidth;
      const style = window.getComputedStyle(this.slider);
      const gap = parseFloat(style.columnGap || style.gap) || 0;
      return slide.getBoundingClientRect().width + gap;
    }

    scroll(direction) {
      const step = this.getStep();
      this.slider.scrollBy({ left: step * direction, behavior: 'smooth' });
    }

    /* 先頭カードの画像の中央(縦)を実測して、矢印の top に渡す */
    updateArrowPosition() {
      if (!this.wrap) return;
      const slide = this.slider.querySelector('.prt-slide');
      if (!slide) return;

      const media =
        slide.querySelector('.card__media') ||
        slide.querySelector('.media') ||
        slide.querySelector('.prt-placeholder-img') ||
        slide.querySelector('img') ||
        slide;

      const wrapRect = this.wrap.getBoundingClientRect();
      const mediaRect = media.getBoundingClientRect();
      // 非表示などで高さが取れないときは何もしない（不正な値を入れない）
      if (!mediaRect.height) return;

      const centerY = mediaRect.top - wrapRect.top + mediaRect.height / 2;
      this.wrap.style.setProperty('--prt-image-center-y', centerY + 'px');
    }

    /* 画像が後から読み込まれた場合にも矢印位置を再計算（重複バインド防止） */
    watchImages() {
      const imgs = this.slider.querySelectorAll('img:not([data-prt-watched])');
      imgs.forEach((img) => {
        img.setAttribute('data-prt-watched', '');
        if (img.complete) return;
        img.addEventListener('load', () => this.updateArrowPosition(), {
          once: true,
        });
      });
    }

    update() {
      // コンテンツがコンテナより広い場合のみ矢印表示
      const hasOverflow = this.slider.scrollWidth > this.slider.clientWidth + 2;
      if (this.prevBtn) {
        if (hasOverflow) this.prevBtn.removeAttribute('hidden');
        else this.prevBtn.setAttribute('hidden', '');
      }
      if (this.nextBtn) {
        if (hasOverflow) this.nextBtn.removeAttribute('hidden');
        else this.nextBtn.setAttribute('hidden', '');
      }
      this.watchImages();
      this.updateArrowPosition();
      this.updateButtons();
    }

    updateButtons() {
      if (!this.prevBtn || !this.nextBtn) return;
      const atStart = this.slider.scrollLeft <= 1;
      const atEnd =
        this.slider.scrollLeft + this.slider.clientWidth >=
        this.slider.scrollWidth - 1;

      this.prevBtn.disabled = atStart;
      this.nextBtn.disabled = atEnd;
    }
  }

  /* ---------- Init ---------- */
  function initAll() {
    document.querySelectorAll('[data-prt-section]').forEach((root) => {
      if (root.dataset.prtInit === 'true') return;
      root.dataset.prtInit = 'true';
      new ProductRecommendationsTabs(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme Editor 対応
  document.addEventListener('shopify:section:load', initAll);
})();